// Attach an AWS IoT policy to a Cognito identity. Required because authenticated
// Cognito identities don't get IoT permissions from their IAM role alone — AWS
// IoT additionally requires an attached IoT policy.
//
// Idempotent: AttachPolicy succeeds if the policy is already attached. We also
// memoize per-identity in sessionStorage so we don't re-fetch on every connect.
//
// Implements SigV4 HTTP signing inline (same crypto primitives as sigv4.js).

const enc = new TextEncoder();
const ATTACHED_KEY = (id) => `rs_iot_policy_attached:${id}`;

async function sha256Hex(buf) {
  const h = await crypto.subtle.digest('SHA-256', typeof buf === 'string' ? enc.encode(buf) : buf);
  return [...new Uint8Array(h)].map(b => b.toString(16).padStart(2, '0')).join('');
}
async function hmacBytes(key, data) {
  const k = await crypto.subtle.importKey('raw', typeof key === 'string' ? enc.encode(key) : key,
    { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return new Uint8Array(await crypto.subtle.sign('HMAC', k, enc.encode(data)));
}
async function deriveKey(secret, dateStamp, region, service) {
  const kDate    = await hmacBytes('AWS4' + secret, dateStamp);
  const kRegion  = await hmacBytes(kDate, region);
  const kService = await hmacBytes(kRegion, service);
  return hmacBytes(kService, 'aws4_request');
}
function amzDate(d = new Date()) {
  const p = (n, w = 2) => String(n).padStart(w, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth()+1)}${p(d.getUTCDate())}` +
         `T${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}Z`;
}

/** Sign an HTTPS request with SigV4 and execute it. Returns parsed JSON or null. */
async function signedFetch({ method, host, path, body, region, service, creds }) {
  const date = amzDate();
  const dateStamp = date.slice(0, 8);
  const scope = `${dateStamp}/${region}/${service}/aws4_request`;
  const payloadHash = await sha256Hex(body || '');

  const headers = {
    'host':                 host,
    'x-amz-date':           date,
    'content-type':         'application/json',
    'x-amz-content-sha256': payloadHash,
  };
  if (creds.sessionToken) headers['x-amz-security-token'] = creds.sessionToken;

  const signedHeaders = Object.keys(headers).sort().join(';');
  const canonicalHeaders = Object.keys(headers).sort().map(k => `${k}:${headers[k]}\n`).join('');

  const canonicalReq = [method, path, '', canonicalHeaders, signedHeaders, payloadHash].join('\n');
  const stringToSign = ['AWS4-HMAC-SHA256', date, scope, await sha256Hex(canonicalReq)].join('\n');
  const signingKey = await deriveKey(creds.secretAccessKey, dateStamp, region, service);
  const sigBytes = await hmacBytes(signingKey, stringToSign);
  const signature = [...sigBytes].map(b => b.toString(16).padStart(2, '0')).join('');

  headers['authorization'] =
    `AWS4-HMAC-SHA256 Credential=${creds.accessKeyId}/${scope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`;

  const res = await fetch(`https://${host}${path}`, { method, headers, body });
  const text = await res.text();
  if (!res.ok) throw new Error(`${method} ${path} → ${res.status}: ${text}`);
  return text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;
}

/**
 * Attach the named IoT policy to the given Cognito identity. Idempotent;
 * memoized per-identity in sessionStorage so it runs once per browser tab.
 */
export async function attachIotPolicyOnce({ region, identityId, policyName, creds }) {
  if (!identityId || !policyName) return;
  const memoKey = ATTACHED_KEY(identityId + ':' + policyName);
  try { if (sessionStorage.getItem(memoKey)) return; } catch {}

  // IoT control-plane endpoint (different from the data-plane *-ats endpoint
  // used for MQTT). SigV4 service name is `execute-api`.
  await signedFetch({
    method:  'PUT',
    host:    `iot.${region}.amazonaws.com`,
    path:    `/target-policies/${encodeURIComponent(policyName)}`,
    body:    JSON.stringify({ target: identityId }),
    region,
    service: 'execute-api',
    creds,
  });

  try { sessionStorage.setItem(memoKey, '1'); } catch {}
}
